// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/**
 * Represents possible string value.
 */
export type MaybeString = string | null | undefined | unknown;

/**
 * Represents a string or null.
 */
export type NullableString = string | null | undefined;

/**
 * Returns whether given value is a string.
 */
export function isString(value: MaybeString): value is string {
    return typeof value === 'string';
}

/**
 * Returns whether given value is a non-empty string.
 */
export function isNonEmptyString(value: MaybeString): value is string {
    return typeof value === 'string' && value.length > 0;
}

/**
 * Returns whether given value is a null or empty string.
 */
export function isNullOrEmptyString(value: MaybeString): value is undefined | null | '' {
    return value === undefined || value === null || (typeof value === 'string' && value.length === 0);
}

/**
 * Compares two nullable strings using current locale.
 */
export function compareLocale(a?: string, b?: string): number {
    return (a ?? '').localeCompare(b ?? '');
}

/**
 * Compares two strings while ignoring case (locale-invariant).
 */
export function compareNoCase(a: string, b: string): number {
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

/**
 * Checks if two strings are equal while ignoring case (locale-invariant).
 */
export function equalsNoCase(a: NullableString, b: NullableString): boolean {
    if (a === undefined || a === null) {
        return b === undefined || b === null;
    } else if (b === undefined || b === null) {
        return false;
    } else {
        return compareNoCase(a, b) === 0;
    }
}

/**
 * Checks if first string has specified prefix while ignoring case (locale-invariant).
 */
export function startsWithNoCase(a: NullableString, b: string): a is string {
    if (a === undefined || a === null) {
        return false;
    } else {
        return a.toLowerCase().startsWith(b.toLowerCase());
    }
}
